/** Kampanya özel gün / sezon anahtarları ve önerilen tarih aralıkları. */

export interface CampaignSpecialDayOption {
  key: string;
  labelTr: string;
  labelEn: string;
  /** Önerilen tarih aralığı üretilebiliyorsa true */
  hasSuggestedDates: boolean;
}

export const CAMPAIGN_SPECIAL_DAYS: CampaignSpecialDayOption[] = [
  { key: '', labelTr: 'Yok / Genel', labelEn: 'None / General', hasSuggestedDates: false },
  { key: 'new-year', labelTr: 'Yılbaşı', labelEn: 'New Year', hasSuggestedDates: true },
  { key: 'valentines', labelTr: 'Sevgililer Günü', labelEn: "Valentine's Day", hasSuggestedDates: true },
  { key: 'mothers-day', labelTr: 'Anneler Günü', labelEn: "Mother's Day", hasSuggestedDates: true },
  { key: 'fathers-day', labelTr: 'Babalar Günü', labelEn: "Father's Day", hasSuggestedDates: true },
  { key: 'childrens-day', labelTr: '23 Nisan / Çocuk Bayramı', labelEn: "Children's Day", hasSuggestedDates: true },
  { key: 'teachers-day', labelTr: 'Öğretmenler Günü', labelEn: "Teachers' Day", hasSuggestedDates: true },
  { key: 'republic-day', labelTr: '29 Ekim Cumhuriyet Bayramı', labelEn: 'Republic Day', hasSuggestedDates: true },
  { key: 'black-friday', labelTr: 'Black Friday', labelEn: 'Black Friday', hasSuggestedDates: true },
  { key: 'cyber-monday', labelTr: 'Cyber Monday', labelEn: 'Cyber Monday', hasSuggestedDates: true },
  { key: 'shopping-1111', labelTr: '11.11 Alışveriş Festivali', labelEn: '11.11 Shopping Festival', hasSuggestedDates: true },
  { key: 'ramadan', labelTr: 'Ramazan', labelEn: 'Ramadan', hasSuggestedDates: false },
  { key: 'eid', labelTr: 'Bayram', labelEn: 'Eid / Holiday', hasSuggestedDates: false },
  { key: 'back-to-school', labelTr: 'Okula Dönüş', labelEn: 'Back to School', hasSuggestedDates: true },
  { key: 'summer-sale', labelTr: 'Yaz İndirimi', labelEn: 'Summer Sale', hasSuggestedDates: true },
  { key: 'winter-sale', labelTr: 'Kış İndirimi', labelEn: 'Winter Sale', hasSuggestedDates: true },
  { key: 'year-end', labelTr: 'Yıl Sonu', labelEn: 'Year End', hasSuggestedDates: true },
  { key: 'custom', labelTr: 'Özel / Diğer', labelEn: 'Custom / Other', hasSuggestedDates: false },
];

const pad = (n: number) => String(n).padStart(2, '0');

const toLocalInput = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const atLocal = (year: number, month: number, day: number, hour = 0, minute = 0): Date =>
  new Date(year, month - 1, day, hour, minute, 0, 0);

/** Ayın n. haftasının verilen günü (0=Pazar). */
const nthWeekdayOfMonth = (year: number, month: number, weekday: number, n: number): Date => {
  const first = new Date(year, month - 1, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month - 1, 1 + offset + (n - 1) * 7);
};

/** Kasım ayındaki son Cuma (Black Friday). */
const lastFridayOfNovember = (year: number): Date => {
  const d = new Date(year, 10, 30);
  while (d.getDay() !== 5) d.setDate(d.getDate() - 1);
  return d;
};

/**
 * Özel güne göre önerilen başlangıç/bitiş (datetime-local string).
 * Değişken günler (ramazan/bayram) için null döner.
 */
export const suggestSpecialDayDates = (
  key: string,
  year = new Date().getFullYear()
): { startDate: string; endDate: string } | null => {
  switch (key) {
    case 'new-year':
      return {
        startDate: toLocalInput(atLocal(year, 12, 26, 0, 0)),
        endDate: toLocalInput(atLocal(year + 1, 1, 2, 23, 59)),
      };
    case 'valentines':
      return {
        startDate: toLocalInput(atLocal(year, 2, 7, 0, 0)),
        endDate: toLocalInput(atLocal(year, 2, 14, 23, 59)),
      };
    case 'mothers-day': {
      const day = nthWeekdayOfMonth(year, 5, 0, 2); // Mayıs 2. Pazar
      const start = new Date(day);
      start.setDate(day.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(day);
      end.setHours(23, 59, 0, 0);
      return { startDate: toLocalInput(start), endDate: toLocalInput(end) };
    }
    case 'fathers-day': {
      const day = nthWeekdayOfMonth(year, 6, 0, 3); // Haziran 3. Pazar
      const start = new Date(day);
      start.setDate(day.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(day);
      end.setHours(23, 59, 0, 0);
      return { startDate: toLocalInput(start), endDate: toLocalInput(end) };
    }
    case 'childrens-day':
      return {
        startDate: toLocalInput(atLocal(year, 4, 15, 0, 0)),
        endDate: toLocalInput(atLocal(year, 4, 23, 23, 59)),
      };
    case 'teachers-day':
      return {
        startDate: toLocalInput(atLocal(year, 11, 20, 0, 0)),
        endDate: toLocalInput(atLocal(year, 11, 24, 23, 59)),
      };
    case 'republic-day':
      return {
        startDate: toLocalInput(atLocal(year, 10, 25, 0, 0)),
        endDate: toLocalInput(atLocal(year, 10, 29, 23, 59)),
      };
    case 'black-friday': {
      const friday = lastFridayOfNovember(year);
      const start = new Date(friday);
      start.setDate(friday.getDate() - 3);
      start.setHours(0, 0, 0, 0);
      const end = new Date(friday);
      end.setHours(23, 59, 0, 0);
      return { startDate: toLocalInput(start), endDate: toLocalInput(end) };
    }
    case 'cyber-monday': {
      const friday = lastFridayOfNovember(year);
      const monday = new Date(friday);
      monday.setDate(friday.getDate() + 3);
      monday.setHours(0, 0, 0, 0);
      const end = new Date(monday);
      end.setHours(23, 59, 0, 0);
      return { startDate: toLocalInput(monday), endDate: toLocalInput(end) };
    }
    case 'shopping-1111':
      return {
        startDate: toLocalInput(atLocal(year, 11, 1, 0, 0)),
        endDate: toLocalInput(atLocal(year, 11, 11, 23, 59)),
      };
    case 'back-to-school':
      return {
        startDate: toLocalInput(atLocal(year, 8, 15, 0, 0)),
        endDate: toLocalInput(atLocal(year, 9, 20, 23, 59)),
      };
    case 'summer-sale':
      return {
        startDate: toLocalInput(atLocal(year, 6, 1, 0, 0)),
        endDate: toLocalInput(atLocal(year, 8, 31, 23, 59)),
      };
    case 'winter-sale':
      return {
        startDate: toLocalInput(atLocal(year, 1, 5, 0, 0)),
        endDate: toLocalInput(atLocal(year, 2, 28, 23, 59)),
      };
    case 'year-end':
      return {
        startDate: toLocalInput(atLocal(year, 12, 1, 0, 0)),
        endDate: toLocalInput(atLocal(year, 12, 31, 23, 59)),
      };
    default:
      return null;
  }
};

export const getSpecialDayLabel = (key: string | null | undefined, isTr: boolean): string => {
  if (!key) return isTr ? 'Genel' : 'General';
  const found = CAMPAIGN_SPECIAL_DAYS.find((d) => d.key === key);
  if (!found) return key;
  return isTr ? found.labelTr : found.labelEn;
};
